/**
 * ABDM M3 consent client — calls only the RIS backend adapter.
 */

import { API_ENDPOINTS } from '../../custom/config/api.config';
import { apiGet, apiPost, ApiError, extractApiErrorMessage } from '../../custom/utils/apiClient';
import type { ConsentPurpose } from './abdmConsentConstants';

export interface ConsentSearchParams {
  abhaAddress?: string;
  drName?: string;
  name?: string;
  hiTypes?: string[];
  from?: string;
  to?: string;
  page?: number;
}

export interface ConsentInitPayload {
  abhaAddress: string;
  drName: string;
  fromDate: string;
  toDate: string;
  hiTypes: string[];
  hipId?: string;
  expiryDate: string;
  purpose:
    | ConsentPurpose
    | { code: string; text: string; refUri: string; btgAcknowledged?: boolean };
}

export interface ConsentSession {
  _id?: string;
  status?: string;
  sessionStatus?: string;
  hiTypes?: string[];
  fromDate?: string;
  toDate?: string;
  dataEraseAt?: string;
  identifiers?: {
    name?: string;
    abha_address?: string;
    abhaAddress?: string;
    abha_number?: string;
  };
  reason?: string;
  noData?: boolean;
  _consentStatusTimestamps?: Record<string, string>;
  consentArtifacts?: Array<Record<string, unknown>>;
  dataPushed?: { entries?: Array<Record<string, unknown>> };
  [key: string]: unknown;
}

export interface ConsentSearchResult {
  page: number;
  sessions: ConsentSession[];
  totalCount: number;
}

export interface ConsentAttachment {
  content?: string;
  contentType?: string;
  title?: string;
}

type ApiEnvelope<T> = {
  success?: boolean;
  message?: string;
  data?: T;
  retryable?: boolean;
};

function isApiEnvelope<T>(value: unknown): value is ApiEnvelope<T> {
  return typeof value === 'object' && value !== null && 'data' in value;
}

function unwrapData<T>(envelope: ApiEnvelope<T> | T): T {
  if (isApiEnvelope<T>(envelope)) {
    return envelope.data;
  }
  return envelope;
}

/** Extract consent session id from common M3 / RIS adapter response shapes. */
export function extractConsentSessionId(inner: unknown): string | undefined {
  if (inner == null) {
    return undefined;
  }
  if (typeof inner === 'string' && inner.trim()) {
    return inner.trim();
  }
  if (Array.isArray(inner)) {
    for (const item of inner) {
      const found = extractConsentSessionId(item);
      if (found) {
        return found;
      }
    }
    return undefined;
  }
  if (typeof inner !== 'object') {
    return undefined;
  }
  const obj = inner as Record<string, unknown>;
  const candidates = [obj.consentSessionId, obj.sessionId, obj._id, obj.id];
  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate.trim()) {
      return candidate.trim();
    }
  }
  if (obj.data != null) {
    return extractConsentSessionId(obj.data);
  }
  return undefined;
}

export async function searchConsents(params: ConsentSearchParams): Promise<ConsentSearchResult> {
  const url = API_ENDPOINTS.ABDM_CONSENTS.LIST(params);
  const { data } = await apiGet<ApiEnvelope<ConsentSearchResult>>(url);
  const result = unwrapData(data) || { page: 1, sessions: [], totalCount: 0 };
  return {
    page: Number(result.page) || 1,
    sessions: Array.isArray(result.sessions) ? result.sessions : [],
    totalCount: Number(result.totalCount) || 0,
  };
}

export async function initConsent(payload: ConsentInitPayload): Promise<{
  consentSessionId?: string;
  raw: unknown;
}> {
  try {
    const { data } = await apiPost<ApiEnvelope<Record<string, unknown>>>(
      API_ENDPOINTS.ABDM_CONSENTS.CREATE,
      payload
    );
    const inner = unwrapData(data) || {};
    const consentSessionId = extractConsentSessionId(inner);
    return { consentSessionId, raw: inner };
  } catch (error) {
    if (error instanceof ApiError) {
      const retryable =
        error.statusCode >= 500 ||
        (error.response &&
          typeof error.response === 'object' &&
          (error.response as { retryable?: boolean }).retryable === true);
      throw new ApiError(extractApiErrorMessage(error.response, error.message), error.statusCode, {
        ...(typeof error.response === 'object' ? error.response : {}),
        retryable,
      });
    }
    throw error;
  }
}

export async function getConsentSession(sessionId: string): Promise<ConsentSession> {
  const { data } = await apiGet<ApiEnvelope<ConsentSession>>(
    API_ENDPOINTS.ABDM_CONSENTS.GET_BY_ID(sessionId)
  );
  return unwrapData(data) || {};
}

export async function getConsentAttachment(
  sessionId: string,
  bundleId: string | number,
  num: string | number
): Promise<ConsentAttachment> {
  const { data } = await apiGet<
    ApiEnvelope<{ attachment?: ConsentAttachment } | ConsentAttachment>
  >(API_ENDPOINTS.ABDM_CONSENTS.ATTACHMENT(sessionId, bundleId, num));
  const inner = unwrapData(data);
  if (inner && typeof inner === 'object' && 'attachment' in inner) {
    return (inner as { attachment?: ConsentAttachment }).attachment || {};
  }
  return (inner as ConsentAttachment) || {};
}

export function getConsentErrorMessage(error: unknown, fallback = 'Request failed'): string {
  if (error instanceof ApiError) {
    return error.message || fallback;
  }
  if (error instanceof Error) {
    return error.message || fallback;
  }
  return fallback;
}

export function isRetryableConsentError(error: unknown): boolean {
  if (!(error instanceof ApiError)) {
    return true;
  }
  if (error.statusCode >= 500 || error.statusCode === 0) {
    return true;
  }
  const body = error.response;
  if (body && typeof body === 'object' && 'retryable' in body) {
    return Boolean((body as { retryable?: boolean }).retryable);
  }
  return error.statusCode !== 400 && error.statusCode !== 403;
}
